import { Fragment, useCallback, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { AuthProvider, type AuthUser } from "./auth/AuthProvider";
import { AuthCallback } from "./auth/AuthCallback";
import { useAuth } from "./auth/useAuth";
import { TenantProvider } from "./context/TenantContext";
import { ToastProvider } from "./context/ToastContext";
import { Layout } from "./components/Layout";
import { RequestList } from "./pages/RequestList";
import { RequestDetail } from "./pages/RequestDetail";
import { Review3 } from "./pages/Review3";
import StatsFlowPage from "./pages/StatsFlowPage";
import { AdminTenants } from "./pages/AdminTenants";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminCosts } from "./pages/AdminCosts";
import { AdminPrices } from "./pages/AdminPrices";
import { AdminAzureSync } from "./pages/AdminAzureSync";
import { AdminEmailBatch } from "./pages/AdminEmailBatch";
import { UserDetail } from "./pages/UserDetail";
import { FlowList } from "./pages/FlowList";
import { FlowSettings } from "./pages/FlowSettings";
import { AuditLog } from "./pages/AuditLog";
import { QueuePage } from "./pages/QueuePage";
import { DelegheVerify } from "./pages/Deleghe/DelegheVerify";
import { DelegheStats } from "./pages/Deleghe/DelegheStats";
import { DelegaDetail } from "./pages/Deleghe/DelegaDetail";
import { BscList } from "./pages/Bsc/BscList";
import { BscDetail } from "./pages/Bsc/BscDetail";
import { BscQueue } from "./pages/Bsc/BscQueue";
import { BscStats } from "./pages/Bsc/BscStats";
import BscLessons from "./pages/Bsc/BscLessons";
import { BscSettings } from "./pages/Bsc/BscSettings";
import { DdtList } from "./pages/Ddt/DdtList";
import { DdtDetail } from "./pages/Ddt/DdtDetail";
import { DdtQueue } from "./pages/Ddt/DdtQueue";
import { MopList } from "./pages/Mop/MopList";
import { MopDetail } from "./pages/Mop/MopDetail";
import { RinascenteList } from "./pages/Rinascente/RinascenteList";
import { RinascenteDetail } from "./pages/Rinascente/RinascenteDetail";
import { RinascenteQueue } from "./pages/Rinascente/RinascenteQueue";
import { RinascenteStats } from "./pages/Rinascente/RinascenteStats";
import { RinascenteSettings } from "./pages/Rinascente/RinascenteSettings";
import { ConversazioniList } from "./pages/Conversazioni/ConversazioniList";
import { ConversazioniSessioniList } from "./pages/Conversazioni/ConversazioniSessioniList";
import { ConversazioneDetail } from "./pages/Conversazioni/ConversazioneDetail";
import { ConversazioniSettings } from "./pages/Conversazioni/ConversazioniSettings";
import { ProvaAgenteChat } from "./pages/Conversazioni/ProvaAgenteChat";
import { ProvePraticheList } from "./pages/Conversazioni/ProvePraticheList";
import { canViewRdiQueue, canViewDdtQueue, canConfigure, canAdmin, canManageUsers, canUseDeleghe, canUseBsc, canUseDdt, canUseRinascente, canUseConversazioni, canWorkTickets } from "./auth/permissions";
import { useTenantConfig } from "./hooks/useTenantConfig";
import { AUDIT_FLOW_ORDER } from "./lib/auditFlows";
import type { AuditFlow } from "./api/types";

function RequireCapability({ check, children }: { check: (roles: string[]) => boolean; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!check(user?.roles ?? [])) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireDeleghe({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { config, isLoading } = useTenantConfig();
  if (isLoading) return null;
  const hasModule = config?.modules?.includes("deleghe") ?? false;
  const hasRole = canUseDeleghe(user?.roles ?? []);
  if (!hasModule || !hasRole) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireBsc({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useTenantConfig();
  if (authLoading || configLoading) return null;
  if (!config?.modules?.includes("bsc") || !canUseBsc(user?.roles ?? []))
    return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RequireDdt({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useTenantConfig();
  if (authLoading || configLoading) return null;
  if (!config?.modules?.includes("ddt") || !canUseDdt(user?.roles ?? []))
    return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function RequireMop({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { config, isLoading } = useTenantConfig();
  if (authLoading || isLoading) return null;
  if (config?.tenant_id !== "casadei" || config.mop_enabled !== true || !canViewRdiQueue(user?.roles ?? []))
    return <Navigate to="/" replace />;
  return <Fragment key={config.tenant_id}>{children}</Fragment>;
}

function RequireRinascente({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useTenantConfig();
  if (authLoading || configLoading) return null;
  if (!config?.modules?.includes("rinascente") || !canUseRinascente(user?.roles ?? []))
    return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Modulo Agente Conversazionale (H4): stesso pattern delle altre guardie —
 * gate sul modulo tenant `conversazioni` + ruolo. Nessun ruolo dedicato
 * `conversazioni_operator` esiste ancora nel backend (src/api/permissions.py
 * non lo definisce a oggi): finché non arriva, si allinea a `canConfigure`
 * (platform_admin/admin) più `operator`, che sono già i ruoli che lavorano le
 * pratiche RDI generate da questo modulo. */
function RequireConversazioni({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { config, isLoading: configLoading } = useTenantConfig();
  if (authLoading || configLoading) return null;
  if (!config?.modules?.includes("conversazioni") || !canUseConversazioni(user?.roles ?? []))
    return <Navigate to="/" replace />;
  return <Fragment key={config.tenant_id}>{children}</Fragment>;
}

/**
 * Registro attività: /audit resta l'unica rotta senza guardia (difetto, non
 * una scelta — vedi AGENTS.md). Guardia coerente con le altre Require*: non
 * un modulo/ruolo singolo, ma "l'utente vede almeno un flusso" (stessi
 * capability check che decidono le sotto-voci in Sidebar.tsx). In più, sulla
 * rotta con :flow — protegge anche i due casi che il menu già non genera ma
 * un URL digitato a mano potrebbe raggiungere:
 *   - un flow che non esiste nell'elenco noto → torna a /audit (l'elenco
 *     completo), invece di far partire una richiesta con un valore che il
 *     backend rifiuterebbe comunque con 400;
 *   - "piattaforma" per chi non è platform_admin → stesso posto.
 */
export function RequireAudit({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { flow } = useParams<{ flow?: string }>();
  const roles = user?.roles ?? [];
  const hasAnyFlow =
    canWorkTickets(roles) ||
    canUseDdt(roles) ||
    canUseBsc(roles) ||
    canUseDeleghe(roles) ||
    canUseRinascente(roles) ||
    canUseConversazioni(roles);
  if (!hasAnyFlow) return <Navigate to="/" replace />;
  if (flow && !AUDIT_FLOW_ORDER.includes(flow as AuditFlow)) return <Navigate to="/audit" replace />;
  if (flow === "piattaforma" && !roles.includes("platform_admin")) return <Navigate to="/audit" replace />;
  return <>{children}</>;
}

function DefaultRedirect() {
  const { config, isLoading } = useTenantConfig();
  if (isLoading) return null;
  if (config?.modules?.includes("rdi")) return <RequestList key="all" excludeSubType="allarme,hyperfabbricati" section="rdi" />;
  if (config?.modules?.includes("deleghe")) return <Navigate to="/deleghe" replace />;
  if (config?.modules?.includes("bsc")) return <Navigate to="/bsc" replace />;
  if (config?.modules?.includes("ddt")) return <Navigate to="/ddt" replace />;
  if (config?.modules?.includes("rinascente")) return <Navigate to="/rinascente" replace />;
  if (config?.modules?.includes("conversazioni")) return <Navigate to="/conversazioni" replace />;
  return <div style={{ padding: "var(--space-6)" }}>Nessun modulo abilitato per questo tenant.</div>;
}

function ProtectedRoutes() {
  const { isAuthenticated, isLoading, login, activeTenant } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login();
    }
  }, [isLoading, isAuthenticated, login]);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div className="spinner spinner-lg" />
        <span className="text-muted">Autenticazione in corso…</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // MagicLinkForm is shown by AuthProvider
  }

  return (
    <TenantProvider key={activeTenant || "none"}>
      <Routes>
        <Route element={<Layout />}>
          {/* Index: redirect basato sul primo modulo abilitato del tenant */}
          <Route index element={<DefaultRedirect />} />
          {/* Route RDI (Casadei) */}
          <Route path="allarmi" element={<RequestList key="allarmi" subType="allarme" section="allarme" />} />
          <Route path="hyper" element={<RequestList key="hyper" subType="hyperfabbricati" section="hyper" />} />
          <Route path="requests/:id" element={<RequestDetail />} />
          <Route path="review3/:id" element={<Review3 />} />
          <Route path="mop/requests" element={<RequireMop><RequestList key="mop" origin="mop" /></RequireMop>} />
          <Route path="mop/requests/:id" element={<RequireMop><Review3 origin="mop" /></RequireMop>} />
          <Route path="mop" element={<RequireMop><MopList /></RequireMop>} />
          <Route path="mop/:id" element={<RequireMop><MopDetail /></RequireMop>} />
          <Route
            path="stats"
            element={
              <RequireCapability check={canWorkTickets}>
                <StatsFlowPage flow="rdi" />
              </RequireCapability>
            }
          />
          <Route path="audit" element={<RequireAudit><AuditLog /></RequireAudit>} />
          <Route path="audit/:flow" element={<RequireAudit><AuditLog /></RequireAudit>} />
          <Route path="queue" element={<RequireCapability check={canViewRdiQueue}><QueuePage /></RequireCapability>} />
          <Route path="settings" element={<RequireCapability check={canConfigure}><FlowList /></RequireCapability>} />
          {/* Configurazione dell'Agente Conversazionale: unificata sotto /settings
              (era /conversazioni/settings, isola separata dalla configurazione
              flussi). Stessa guardia di prima (modulo tenant + ruolo). */}
          <Route path="settings/agente" element={<RequireConversazioni><ConversazioniSettings /></RequireConversazioni>} />
          {/* Area di prova dell'agente: tutta sotto /settings/agente/..., mai
              /conversazioni/... — quella rotta accende la voce di sidebar
              "Agente" invece di "Configurazione", dove ci si trova davvero
              mentre si prova l'agente (mismatch segnalato dall'utente). */}
          <Route path="settings/agente/chat" element={<RequireConversazioni><ProvaAgenteChat /></RequireConversazioni>} />
          <Route
            path="settings/agente/sessioni/:id"
            element={<RequireConversazioni><ConversazioneDetail backTo="/settings/agente" /></RequireConversazioni>}
          />
          <Route path="settings/agente/pratiche" element={<RequireConversazioni><ProvePraticheList /></RequireConversazioni>} />
          <Route path="settings/:flowId" element={<RequireCapability check={canConfigure}><FlowSettings /></RequireCapability>} />
          <Route path="admin/tenants" element={<RequireCapability check={canAdmin}><AdminTenants /></RequireCapability>} />
          <Route path="admin/users" element={<RequireCapability check={canManageUsers}><AdminUsers /></RequireCapability>} />
          <Route path="admin/users/:userId" element={<RequireCapability check={canManageUsers}><UserDetail /></RequireCapability>} />
          <Route path="admin/costs" element={<RequireCapability check={canAdmin}><AdminCosts /></RequireCapability>} />
          <Route path="admin/prices" element={<RequireCapability check={canAdmin}><AdminPrices /></RequireCapability>} />
          <Route path="admin/azure-sync" element={<RequireCapability check={canConfigure}><AdminAzureSync /></RequireCapability>} />
          <Route path="admin/email-batch" element={<RequireCapability check={canConfigure}><AdminEmailBatch /></RequireCapability>} />
          {/* Route Deleghe (Acquirente Unico) */}
          <Route path="deleghe" element={<RequireDeleghe><DelegheVerify /></RequireDeleghe>} />
          <Route path="deleghe/stats" element={<RequireDeleghe><DelegheStats /></RequireDeleghe>} />
          <Route path="deleghe/:checkId" element={<RequireDeleghe><DelegaDetail /></RequireDeleghe>} />
          {/* Route BSC (Frabemar) */}
          <Route path="bsc" element={<RequireBsc><BscList /></RequireBsc>} />
          <Route path="bsc/queue" element={<RequireBsc><BscQueue /></RequireBsc>} />
          <Route path="bsc/stats" element={<RequireBsc><BscStats /></RequireBsc>} />
          <Route path="bsc/lessons" element={<RequireBsc><BscLessons /></RequireBsc>} />
          <Route path="bsc/settings" element={<RequireBsc><BscSettings /></RequireBsc>} />
          <Route path="bsc/:practiceId" element={<RequireBsc><BscDetail /></RequireBsc>} />
          {/* Route DDT (Casadei) */}
          <Route path="ddt" element={<RequireDdt><DdtList /></RequireDdt>} />
          <Route path="ddt/stats" element={<RequireDdt><StatsFlowPage flow="ddt" /></RequireDdt>} />
          {/* Coda output DDT (Item 1, 2026-07-14): permesso dedicato canViewDdtQueue,
              non canUseDdt/RequireDdt — un editor senza ruolo ddt_operator deve
              poterla vedere (gestisce la coda), ma non l'elenco pratiche DDT. */}
          <Route path="ddt/queue" element={<RequireCapability check={canViewDdtQueue}><DdtQueue /></RequireCapability>} />
          <Route path="ddt/:id" element={<RequireDdt><DdtDetail /></RequireDdt>} />
          {/* Route Rinascente / La Rinascente */}
          <Route path="rinascente" element={<RequireRinascente><RinascenteList /></RequireRinascente>} />
          <Route path="rinascente/queue" element={<RequireRinascente><RinascenteQueue /></RequireRinascente>} />
          <Route path="rinascente/stats" element={<RequireRinascente><RinascenteStats /></RequireRinascente>} />
          <Route path="rinascente/settings" element={<RequireRinascente><RinascenteSettings /></RequireRinascente>} />
          <Route path="rinascente/:id" element={<RequireRinascente><RinascenteDetail /></RequireRinascente>} />
          {/* Route Conversazioni (Agente Conversazionale, H4) — struttura a
              schede: "conversazioni/sessioni" e' un segmento letterale, quindi
              vince su ":id" indipendentemente dall'ordine di dichiarazione
              (stesso pattern di "ddt/queue" prima di "ddt/:id"), ma lo si
              dichiara comunque per primo per leggibilita'. */}
          <Route path="conversazioni" element={<RequireConversazioni><ConversazioniList /></RequireConversazioni>} />
          <Route path="conversazioni/sessioni" element={<RequireConversazioni><ConversazioniSessioniList /></RequireConversazioni>} />
          <Route
            path="conversazioni/sessioni/:id"
            element={<RequireConversazioni><ConversazioneDetail backTo="/conversazioni/sessioni" /></RequireConversazioni>}
          />
          <Route path="conversazioni/:id" element={<RequireConversazioni><ConversazioneDetail /></RequireConversazioni>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </TenantProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  // If already authenticated and hitting /auth/callback, just go home
  if (isAuthenticated) {
    return (
      <Routes>
        <Route path="auth/callback" element={<Navigate to="/" replace />} />
        <Route path="*" element={<ProtectedRoutes />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="auth/callback" element={<AuthCallbackRoute />} />
      <Route path="*" element={<ProtectedRoutes />} />
    </Routes>
  );
}

function AuthCallbackRoute() {
  // We can't easily get handleLoginSuccess here because it's internal to
  // MagicLinkAuthProvider. But we CAN trigger the same effect by saving the
  // session to sessionStorage and reloading — the provider will pick it up.
  const navigate = useNavigate();

  const handleSuccess = useCallback((token: string, user: AuthUser) => {
    // Replicate what MagicLinkAuthProvider.handleLoginSuccess does:
    const payload = (() => {
      try {
        const parts = token.split(".");
        if (parts.length !== 3) return {};
        return JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
      } catch { return {}; }
    })();
    const exp = (payload.exp as number) || Math.floor(Date.now() / 1000) + 43200;
    sessionStorage.setItem("ts_auth", JSON.stringify({ token, user, exp }));
    // Navigate to / — AuthProvider will pick up the session from storage
    navigate("/", { replace: true });
    window.location.reload();
  }, [navigate]);

  return <AuthCallback onSuccess={handleSuccess} />;
}
